import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronDown, faChevronUp, faLayerGroup } from '@fortawesome/free-solid-svg-icons';

import { WorkItem, showsScamMark } from '../api/workItems';
import ScamMark from '../components/ScamMark';
import { getWorkItemDisplayTitle } from '../utils/workItemDisplay';

export interface WatchStack {
  key: string;
  label: string;
  items: WorkItem[];
}

export function watchUpdateCount(item: WorkItem) {
  const merged = Number((item.meta as { thread_merge?: { merged_count?: number } } | undefined)?.thread_merge?.merged_count || 0);
  return 1 + (Number.isFinite(merged) && merged > 0 ? merged : 0);
}

export interface TodayWatchStacksProps {
  watchStacks: WatchStack[];
  watchStreamsExpanded: boolean;
  onToggleWatchStreams: () => void;
  watchStackError: string | null;
  expandedWatchStacks: Set<string>;
  archivingWatchStacks: Set<string>;
  onToggleStack: (sourceKey: string) => void;
  onArchiveStack: (stack: WatchStack) => void;
  onOpenItem: (item: WorkItem, activator?: HTMLElement | null) => void;
  openDrawerId?: string | null;
}

/**
 * Module-level so the watch-stream scroller keeps its scroll position when
 * Today re-renders (same remount trap as the board columns).
 */
export const TodayWatchStacks = React.memo(function TodayWatchStacks({
  watchStacks,
  watchStreamsExpanded,
  onToggleWatchStreams,
  watchStackError,
  expandedWatchStacks,
  archivingWatchStacks,
  onToggleStack,
  onArchiveStack,
  onOpenItem,
  openDrawerId = null,
}: TodayWatchStacksProps) {
  if (watchStacks.length === 0) return null;
  // C29-1 AC10: Watch streams and each stack start collapsed, so the mark shows on the
  // collapsed headers too.
  const markedIn = (stack: WatchStack) => stack.items.filter(item => showsScamMark(item.safety)).length;
  const markedTotal = watchStacks.reduce((count, stack) => count + markedIn(stack), 0);

  return (
    <section
      className="mb-6 rounded-2xl border border-sky-500/15 bg-sky-500/[0.035] p-3 md:p-4"
      data-testid="today-watch-stacks"
      aria-labelledby="today-watch-stacks-title"
    >
      <button
        type="button"
        data-testid="today-watch-stacks-toggle"
        className="mb-1 flex w-full flex-wrap items-center justify-between gap-2 rounded-xl px-1 py-1 text-left hover:bg-sky-500/[0.06]"
        onClick={onToggleWatchStreams}
        aria-expanded={watchStreamsExpanded}
        aria-controls="today-watch-stacks-grid"
      >
        <div>
          <h2
            id="today-watch-stacks-title"
            className="flex items-center gap-2 text-sm font-semibold text-theme"
          >
            <FontAwesomeIcon icon={faLayerGroup} className="text-chip-info" aria-hidden />
            Watch streams
          </h2>
          <p className="mt-0.5 text-[11px] text-theme-muted">
            Repeat updates stay grouped here and do not spend Triage cards.
          </p>
        </div>
        <span className="flex items-center gap-2">
          {markedTotal > 0 && <ScamMark data-testid="WB_TODAY_WATCH_RISK_MARK" count={markedTotal} />}
          <span className="rounded-full border border-sky-500/20 bg-sky-500/10 px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider text-chip-info">
            {watchStacks.reduce((count, stack) => count + stack.items.reduce((sum, item) => sum + watchUpdateCount(item), 0), 0)} updates
          </span>
          <FontAwesomeIcon
            icon={watchStreamsExpanded ? faChevronUp : faChevronDown}
            className="text-[10px] text-theme-muted"
            aria-hidden
          />
        </span>
      </button>
      {watchStackError && (
        <p className="mb-3 px-1 text-[11px] font-medium text-danger" role="alert">
          {watchStackError}
        </p>
      )}

      {watchStreamsExpanded && (
        <div
          id="today-watch-stacks-grid"
          data-testid="today-watch-stacks-grid"
          className="mt-2 max-h-[min(40vh,24rem)] overflow-y-auto pr-1"
        >
          <div className="grid grid-cols-1 gap-2 md:grid-cols-2 xl:grid-cols-3">
            {watchStacks.map(stack => {
              const isExpanded = expandedWatchStacks.has(stack.key);
              const isArchiving = archivingWatchStacks.has(stack.key);
              const listId = `today-watch-stack-list-${stack.key}`;

              return (
                <div
                  key={stack.key}
                  data-testid={`today-watch-stack-${stack.key}`}
                  className="overflow-hidden rounded-xl border border-sky-500/15 bg-theme-card shadow-sm"
                >
                  <div className="flex items-center gap-2 p-2">
                    <button
                      type="button"
                      data-testid={`today-watch-stack-toggle-${stack.key}`}
                      className="flex min-w-0 flex-1 items-center justify-between gap-3 rounded-lg px-2 py-2 text-left hover:bg-sky-500/[0.06]"
                      onClick={() => onToggleStack(stack.key)}
                      aria-expanded={isExpanded}
                      aria-controls={listId}
                    >
                      <span className="min-w-0 truncate text-sm font-semibold text-theme">
                        {stack.label} · {stack.items.reduce((sum, item) => sum + watchUpdateCount(item), 0)} new
                      </span>
                      {markedIn(stack) > 0 && (
                        <ScamMark data-testid="WB_WATCH_STACK_RISK_MARK" count={markedIn(stack)} />
                      )}
                      <FontAwesomeIcon
                        icon={isExpanded ? faChevronUp : faChevronDown}
                        className="shrink-0 text-[10px] text-theme-muted"
                        aria-hidden
                      />
                    </button>
                    <button
                      type="button"
                      data-testid={`today-watch-stack-archive-${stack.key}`}
                      className="shrink-0 rounded-lg border border-theme px-2.5 py-2 text-[10px] font-semibold text-theme-secondary hover:border-sky-500/30 hover:text-chip-info disabled:cursor-wait disabled:opacity-50"
                      onClick={() => onArchiveStack(stack)}
                      disabled={isArchiving}
                      aria-label={`Archive all ${stack.label} updates`}
                    >
                      {isArchiving ? 'Archiving…' : 'Archive all'}
                    </button>
                  </div>

                  {isExpanded && (
                    <ul
                      id={listId}
                      data-testid={listId}
                      className="border-t border-sky-500/10 px-2 pb-2"
                    >
                      {stack.items.map(item => (
                        <li key={item.id}>
                          <button
                            type="button"
                            data-testid={`today-watch-stack-item-${item.id}`}
                            data-drawer-open={openDrawerId === item.id ? 'true' : undefined}
                            className="mt-2 flex w-full items-start justify-between gap-3 rounded-lg px-3 py-2 text-left hover:bg-sky-500/[0.06]"
                            style={openDrawerId === item.id ? {
                              outline: '2px solid rgba(var(--color-accent-rgb), 0.55)',
                              boxShadow: '0 0 0 3px rgba(var(--color-accent-rgb), 0.35)',
                            } : undefined}
                            onClick={event => onOpenItem(item, event.currentTarget)}
                          >
                            <span className="min-w-0">
                              <span className="block truncate text-xs font-medium text-theme">
                                {getWorkItemDisplayTitle(item)}
                              </span>
                              {item.summary && (
                                <span className="mt-0.5 block truncate text-[10px] text-theme-muted">
                                  {item.summary}
                                </span>
                              )}
                            </span>
                            <span className="flex shrink-0 items-center gap-2 pt-0.5">
                              {showsScamMark(item.safety) && <ScamMark data-testid="WB_ROW_RISK_MARK" />}
                              <span className="text-[9px] font-bold uppercase tracking-wider text-chip-info">
                                Open
                              </span>
                            </span>
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </section>
  );
});
